import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Customer, UnbilledCharge } from '../../types';
import {
  Zap,
  TrendingUp,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  Check,
  ArrowUpRight
} from 'lucide-react';

interface PortalUsageProps {
  customer: Customer;
}

const MOCK_USAGE_HISTORY = [
  { date: '24 May', usage: 1200 },
  { date: '25 May', usage: 1450 },
  { date: '26 May', usage: 1100 },
  { date: '27 May', usage: 1900 },
  { date: '28 May', usage: 2300 },
  { date: '29 May', usage: 1700 },
  { date: '30 May', usage: 2100 },
];

const MOCK_UNBILLED: UnbilledCharge[] = [
  { id: 'ub_1', description: 'Additional Seat (Pro Workspace)', amount: 15.00, date: '2024-05-28' },
  { id: 'ub_2', description: 'Priority SLA & Dedicated Compute Add-on', amount: 50.00, date: '2024-05-30' },
];

const CREDIT_PACKS = [
  { id: 'p1', amount: 5000, price: 49, label: 'Starter Booster', perUnit: '$0.0098/credit', savings: null },
  { id: 'p2', amount: 25000, price: 199, label: 'Growth Accelerator', perUnit: '$0.0079/credit', savings: 'Save 20%', popular: true },
  { id: 'p3', amount: 100000, price: 599, label: 'Scale Enterprise Volume', perUnit: '$0.0059/credit', savings: 'Save 40%', bonus: '+10,000 Bonus Credits' },
];

const PortalUsage: React.FC<PortalUsageProps> = ({ customer }) => {
  const [alertThreshold, setAlertThreshold] = useState(80);
  const [isBuying, setIsBuying] = useState(false);
  const [selectedPack, setSelectedPack] = useState(CREDIT_PACKS[1].id);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseSuccessMessage, setPurchaseSuccessMessage] = useState<string | null>(null);
  
  const [currentCustomer, setCurrentCustomer] = useState(customer);

  const percentage = Math.min(100, Math.round((currentCustomer.credits.used / Math.max(1, currentCustomer.credits.totalAllocated)) * 100));
  const projectedBill = 49.00 + MOCK_UNBILLED.reduce((s, c) => s + c.amount, 0);

  const handlePurchase = async () => {
    setIsProcessing(true);
    const pack = CREDIT_PACKS.find(p => p.id === selectedPack)!;
    await new Promise(r => setTimeout(r, 1200));
    
    setCurrentCustomer(prev => ({
      ...prev,
      credits: {
        ...prev.credits,
        balance: prev.credits.balance + pack.amount,
        totalAllocated: prev.credits.totalAllocated + pack.amount
      }
    }));

    setIsProcessing(false);
    setIsBuying(false);
    setPurchaseSuccessMessage(`🎉 Successfully added ${pack.amount.toLocaleString()} credits to your workspace. Your execution pipelines are operating at 100% capacity.`);
    setTimeout(() => setPurchaseSuccessMessage(null), 6000);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* High Usage Proactive Upgrade Banner */}
      {percentage >= 70 && (
        <div className="bg-gradient-to-r from-amber-500/15 via-indigo-500/10 to-emerald-500/10 border border-amber-300 dark:border-amber-700/60 p-6 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="p-2.5 bg-amber-500 text-white rounded-2xl shadow-sm shrink-0 mt-0.5">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                  High Usage Velocity Detected ({percentage}% Used)
                </h4>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-900 rounded-full">
                  Upgrade Recommendation
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 max-w-xl font-medium">
                Your team is scaling fast! Upgrading your base subscription plan gives you <strong>5x more included credits</strong> at 45% lower cost per execution, plus priority model latency.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <button
              onClick={() => setIsBuying(true)}
              className="px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm cursor-pointer"
            >
              Top-Up Credits
            </button>
            <button
              onClick={() => {
                const subTab = document.querySelector('[data-tab="subscription"]') as HTMLElement;
                if (subTab) subTab.click();
              }}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              Upgrade Plan
            </button>
          </div>
        </div>
      )}

      {purchaseSuccessMessage && (
        <div className="p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-2xl text-xs font-bold flex items-center gap-3 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>{purchaseSuccessMessage}</span>
        </div>
      )}

      {/* Main Charts & Allocation KPI */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-[2.5rem] p-8 sm:p-10 border border-slate-200 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-slate-900 tracking-tight uppercase">Resource Consumption</h3>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-600">
                  Real-time Metering
                </span>
              </div>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">7-Day Trailing Credit Velocity</p>
            </div>
            <div className="bg-slate-900 text-white px-6 py-3 rounded-2xl flex flex-col items-end">
              <p className="text-[9px] font-black uppercase text-indigo-400 tracking-widest">Est. Next Invoice</p>
              <p className="text-xl font-black">${projectedBill.toFixed(2)}</p>
            </div>
          </div>

          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={MOCK_USAGE_HISTORY}>
                <defs>
                  <linearGradient id="usageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700, fill: '#94a3b8'}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8'}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}
                  itemStyle={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}
                />
                <Area type="monotone" dataKey="usage" stroke="#4f46e5" strokeWidth={3} fillOpacity={1} fill="url(#usageGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
            <div className="p-3.5 bg-slate-50 rounded-2xl">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Daily Burn Rate</span>
              <span className="text-base font-black text-slate-900">~1,420 credits/day</span>
            </div>
            <div className="p-3.5 bg-slate-50 rounded-2xl">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Projected Rollover</span>
              <span className="text-base font-black text-emerald-600">+8,400 credits</span>
            </div>
            <div className="p-3.5 bg-slate-50 rounded-2xl col-span-2 sm:col-span-1">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Auto-Refill</span>
              <span className="text-base font-black text-indigo-600">Active on 1st</span>
            </div>
          </div>
        </div>

        {/* Allocation Health & Quick Top-Up Widget */}
        <div className="bg-gradient-to-b from-slate-900 to-indigo-950 rounded-[2.5rem] p-8 sm:p-10 text-white flex flex-col justify-between shadow-2xl relative overflow-hidden group">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Allocation Health</p>
              <span className="text-[10px] font-bold text-slate-300 bg-white/10 px-2.5 py-1 rounded-full">
                {currentCustomer.credits.remaining.toLocaleString()} left
              </span>
            </div>

            <div className="space-y-6">
              <div className="flex justify-between items-end">
                <p className="text-5xl font-black">{percentage}%</p>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">
                  {currentCustomer.credits.used.toLocaleString()} / {currentCustomer.credits.totalAllocated.toLocaleString()}
                </p>
              </div>
              <div className="w-full h-4 bg-white/10 rounded-full overflow-hidden p-0.5">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400 rounded-full shadow-[0_0_15px_rgba(79,70,229,0.5)] transition-all duration-1000"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
          
          <div className="relative z-10 mt-8 space-y-4">
            <div className="bg-white/5 p-4 rounded-2xl border border-white/10">
              <div className="flex justify-between mb-2">
                <p className="text-[10px] font-black uppercase text-slate-400">Proactive Alert Threshold</p>
                <p className="text-[10px] font-black uppercase text-indigo-400">{alertThreshold}%</p>
              </div>
              <input 
                type="range" min="50" max="100" value={alertThreshold} 
                onChange={e => setAlertThreshold(parseInt(e.target.value))}
                className="w-full accent-indigo-500 cursor-pointer" 
              />
            </div>

            <button 
              onClick={() => setIsBuying(true)}
              className="w-full py-4 bg-white hover:bg-slate-100 text-indigo-950 rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Zap className="w-4 h-4 text-indigo-600" />
              Purchase Credit Booster Pack
            </button>
            <p className="text-center text-[10px] font-bold text-indigo-300">
              Never expire • Instant zero-downtime provisioning
            </p>
          </div>
        </div>
      </div>

      {/* Unbilled Charges & Value Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-[2.5rem] p-8 sm:p-10 border border-slate-200 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Unbilled Usage & Add-ons</h3>
            <span className="text-[10px] font-bold text-slate-400">Applied on next cycle</span>
          </div>
          <div className="space-y-3">
            {MOCK_UNBILLED.map(ub => (
              <div key={ub.id} className="flex justify-between items-center p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                  <p className="text-xs font-bold text-slate-900">{ub.description}</p>
                  <p className="text-[10px] text-slate-400 font-medium">Activated on {ub.date}</p>
                </div>
                <span className="text-xs font-black text-indigo-600">${ub.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-slate-50 rounded-[2.5rem] p-8 sm:p-10 border border-indigo-100/80 shadow-sm flex flex-col justify-between space-y-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-black uppercase tracking-wider mb-2">
              <Sparkles className="w-3 h-3" /> Volume Upgrade Benefit
            </div>
            <h3 className="text-base font-black text-slate-900 uppercase tracking-tight">
              Looking for Volume Discounts?
            </h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Teams running over 50,000 executions per month save up to <strong>60% annually</strong> by upgrading to custom volume tiers with dedicated SLA guarantees.
            </p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-xs font-bold text-slate-500">Custom enterprise billing available</span>
            <button
              onClick={() => alert('Our enterprise solution architect will reach out within 2 hours with customized tier pricing.')}
              className="text-xs font-black text-indigo-600 uppercase tracking-wider hover:underline inline-flex items-center gap-1 cursor-pointer"
            >
              Request Custom Tier <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Credit Booster Pack Purchase Modal */}
      {isBuying && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md" onClick={() => setIsBuying(false)}></div>
          <div className="relative bg-white rounded-[3rem] shadow-2xl w-full max-w-2xl p-8 sm:p-10 animate-in zoom-in-95 space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">
                  Instant Quota Top-Up
                </span>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight uppercase mt-0.5">
                  Select Your Credit Booster Pack
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Credits activate immediately on your workspace and never expire.
                </p>
              </div>
              <button
                onClick={() => setIsBuying(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 hover:text-slate-800 flex items-center justify-center text-sm font-bold"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CREDIT_PACKS.map(pack => (
                <div 
                  key={pack.id}
                  onClick={() => setSelectedPack(pack.id)}
                  className={`p-6 rounded-3xl border-2 text-left transition-all relative cursor-pointer flex flex-col justify-between ${
                    selectedPack === pack.id
                      ? 'border-indigo-600 bg-indigo-50/50 shadow-lg ring-2 ring-indigo-500/20'
                      : 'border-slate-100 hover:border-slate-200 bg-white'
                  }`}
                >
                  {pack.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-[8px] font-black uppercase px-2.5 py-1 rounded-full shadow-sm">
                      Best Value • Top Pick
                    </span>
                  )}
                  {pack.savings && (
                    <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md w-fit mb-2">
                      {pack.savings}
                    </span>
                  )}
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{pack.label}</p>
                    <p className="text-2xl font-black text-slate-900">{pack.amount.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-500 font-semibold mt-0.5">{pack.perUnit}</p>
                  </div>
                  <div className="pt-4 border-t border-slate-100 mt-4 flex items-center justify-between">
                    <span className="text-base font-black text-indigo-600">${pack.price}</span>
                    <span className="text-[9px] font-bold text-slate-400 uppercase">One-time</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-slate-700">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-black text-slate-900 uppercase">Visa •••• 4242 (Default Card)</p>
                  <p className="text-[10px] text-slate-400 font-bold">Encrypted 256-bit instant checkout</p>
                </div>
              </div>
              <span className="text-[10px] font-black text-emerald-600 uppercase bg-emerald-50 px-2.5 py-1 rounded-lg">
                Verified
              </span>
            </div>

            <div className="flex gap-4 pt-2">
              <button
                onClick={() => setIsBuying(false)}
                className="flex-1 py-4 font-black text-slate-500 hover:text-slate-800 text-xs uppercase tracking-widest cursor-pointer"
              >
                Cancel
              </button>
              <button 
                onClick={handlePurchase}
                disabled={isProcessing}
                className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                {isProcessing ? (
                  <span>Authorizing & Allocating...</span>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    <span>Authorize ${CREDIT_PACKS.find(p => p.id === selectedPack)?.price} • Activate Now</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalUsage;
